import type { ComponentType } from 'react'
import type { LucideIcon } from 'lucide-react'
import type {
  AdjustmentsParams,
  CompositionNode,
  CropParams,
  ImageNode,
  OrientationParams,
  TransformKind,
} from '@/core/document'
import type { AspectRatio } from '@/core/geometry'
import type { SourceImage } from '@/core/image'
import { ToolTab } from '@/core/store/uiState'

export { ToolTab } from '@/core/store/uiState'

export enum ToolKind {
  Transform = 'transform',
  Node = 'node',
  Output = 'output',
  Utility = 'utility',
}

export enum UtilityId {
  Preview = 'preview',
  Quality = 'quality',
}

interface ToolDefinition {
  tab: ToolTab
  label: string
  icon: LucideIcon
  appliesTo(activePhoto: ImageNode | null): boolean
  Panel: ComponentType
}

interface TransformParamsByKind {
  [TransformKind.Orientation]: OrientationParams
  [TransformKind.Crop]: CropParams
  [TransformKind.Adjustments]: AdjustmentsParams
}

export interface TransformDefinition<K extends TransformKind> {
  createDefault(source: SourceImage, ratio: AspectRatio): TransformParamsByKind[K]
  summarize(params: TransformParamsByKind[K]): string
}

export type TransformDefinitions = {
  [K in TransformKind]?: TransformDefinition<K>
}

export interface TransformTool extends ToolDefinition {
  kind: ToolKind.Transform
  transforms: readonly TransformKind[]
  definitions: TransformDefinitions
}

export interface OutputTool extends ToolDefinition {
  kind: ToolKind.Output
}

export interface NodeTool extends ToolDefinition {
  kind: ToolKind.Node
  node: CompositionNode['kind']
}

export interface UtilityTool {
  id: UtilityId
  kind: ToolKind.Utility
  label: string
  icon: LucideIcon
  Overlay: ComponentType
}

export type Tool = TransformTool | NodeTool | OutputTool
